import { useState } from 'preact/hooks';
import { STEPS } from '../data/content';
import { useReveal } from '../hooks/useReveal';
import './HowItWorks.scss';

/**
 * Three-step "how it works." Each step is a glass card with a big
 * tabular numeral, a lowercase title, a body paragraph, and (for
 * step 1) a tiny code snippet. The numerals are the visual hook —
 * styled in the aurora-text gradient at a size that earns it.
 */
export function HowItWorks() {
	const ref = useReveal<HTMLDivElement>();
	return (
		<section class="m-how" id="how">
			<div class="m-how__inner" ref={ref}>
				<header class="m-how__head">
					<span class="m-how__eyebrow">how it works</span>
					<h2 class="m-how__title">three steps to live feedback.</h2>
					<p class="m-how__sub">
						No SDK to learn, no schema migration, no rebuild. Drop the script, anchor a
						comment, ship.
					</p>
				</header>
				<ol class="m-how__steps">
					{STEPS.map((step, i) => (
						<li
							class={`m-how__step ${i === 0 ? 'm-how__step--wide' : ''}`}
							key={step.n}
						>
							<span class="m-how__num">{step.n}</span>
							<h3 class="m-how__step-title">{step.title}</h3>
							<p class="m-how__step-body">{step.body}</p>
							{step.code && <CopyCode code={step.code} />}
							{step.link && (
								<a class="m-how__step-link" href={step.link.href}>
									{step.link.label}
									<span class="m-how__step-link-arrow" aria-hidden="true">→</span>
								</a>
							)}
						</li>
					))}
				</ol>
			</div>
		</section>
	);
}

/**
 * Pre-formatted code box with a copy-to-clipboard icon button. The
 * scrollbar is themed via the global ::-webkit-scrollbar block in
 * styles/index.scss so it matches every other scroll surface on the
 * page. After a copy the button briefly flips to a checkmark + the
 * "copied" label fades in so the affordance closes its own loop.
 */
function CopyCode({ code }: { code: string }) {
	const [copied, setCopied] = useState(false);
	const onCopy = () => {
		void navigator.clipboard.writeText(code).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 1600);
		});
	};
	return (
		<div class="m-how__code-wrap">
			<pre class="m-how__step-code">
				<code>{code}</code>
			</pre>
			<button
				type="button"
				class={`m-how__copy ${copied ? 'm-how__copy--ok' : ''}`}
				onClick={onCopy}
				aria-label={copied ? 'Copied' : 'Copy snippet'}
			>
				<span class="m-how__copy-icon" aria-hidden="true" />
				<span class="m-how__copy-label">{copied ? 'copied' : 'copy'}</span>
			</button>
		</div>
	);
}
